import { useState, useEffect } from 'react';

/**
 * Get the total spacing (margin + border + padding) for a given CSS class
 * @param {string} className - The CSS class name to analyze
 * @returns {Object} Object with horizontal and vertical spacing values
 */
const getClassSpacing = (className) => {
  if (typeof document === 'undefined') {
    return { horizontal: 0, vertical: 0 };
  }

  // Create a temporary element with the class
  const tempElement = document.createElement('div');
  tempElement.className = className;
  tempElement.style.visibility = 'hidden';
  tempElement.style.position = 'absolute';
  tempElement.style.top = '-9999px';
  document.body.appendChild(tempElement);

  try {
    const computedStyle = window.getComputedStyle(tempElement);

    // Get margin values
    const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
    const marginRight = parseFloat(computedStyle.marginRight) || 0;
    const marginTop = parseFloat(computedStyle.marginTop) || 0;
    const marginBottom = parseFloat(computedStyle.marginBottom) || 0;

    const horizontal = marginLeft + marginRight;
    const vertical = marginTop + marginBottom;

    return { horizontal, vertical };
  } finally {
    document.body.removeChild(tempElement);
  }
};

/**
 * Custom hook to calculate video container and video element styles
 * for properly positioning and scaling video regions
 * 
 * @param {Object} params
 * @param {Object} params.regionBounds - The region bounds to display
 * @param {number} params.regionBounds.left - Left position of region
 * @param {number} params.regionBounds.top - Top position of region
 * @param {number} params.regionBounds.width - Width of region
 * @param {number} params.regionBounds.height - Height of region
 * @param {Object} params.viewportSize - The viewport dimensions
 * @param {number|string} params.viewportSize.width - Viewport width
 * @param {number|string} params.viewportSize.height - Viewport height
 * @param {Object} params.videoMetadata - The original video dimensions
 * @param {number} params.videoMetadata.width - Original video width
 * @param {number} params.videoMetadata.height - Original video height
 * @param {boolean} params.isVideoReady - Whether the video is ready
 * @param {string} params.videoContainerClassName - CSS class for container spacing calculation
 * @param {boolean} params.showDebug - Whether to log debug information
 * @returns {Object} Object containing containerStyle and videoStyle
 */
export function useVideoContainerStyles({
  regionBounds,
  viewportSize,
  videoMetadata,
  isVideoReady,
  videoContainerClassName = 'video-container-wrapper-mask',
  showDebug = false,
}) {
  const [containerStyle, setContainerStyle] = useState({});
  const [videoStyle, setVideoStyle] = useState({});
  const [classSpacing, setClassSpacing] = useState({ horizontal: 0, vertical: 0 });
  const [actualSize, setActualSize] = useState({ width: 0, height: 0 });

  // Calculate spacing from the video container class
  useEffect(() => {
    const spacing = getClassSpacing(videoContainerClassName);
    setClassSpacing(spacing);
  }, [videoContainerClassName]);

  // Calculate video positioning and scaling
  useEffect(() => {
    if (
      !isVideoReady ||
      !regionBounds ||
      !viewportSize.width ||
      !viewportSize.height
    )
      return;

    // Use the actual video dimensions from metadata
    const originalVideoWidth = videoMetadata.width;
    const originalVideoHeight = videoMetadata.height;
    
    if (showDebug) {
      console.log(
        'Using video dimensions:',
        originalVideoWidth,
        originalVideoHeight
      );
    }
    
    // Calculate the aspect ratio of the region
    const regionAspectRatio = regionBounds.width / regionBounds.height;

    // Calculate the aspect ratio of the viewport
    const viewportAspectRatio = viewportSize.width / viewportSize.height;

    // Determine how to scale and position the video region within the viewport
    let scaledWidth, scaledHeight;

    if (viewportAspectRatio > regionAspectRatio) {
      // Viewport is wider than region - fit to height
      scaledHeight = viewportSize.height;
      scaledWidth = viewportSize.height * regionAspectRatio;
    } else {
      // Viewport is taller than region - fit to width
      scaledWidth = viewportSize.width;
      scaledHeight = viewportSize.width / regionAspectRatio;
    }

    // Calculate the position to center the scaled region in the viewport
    const horizontalOffset = (viewportSize.width - scaledWidth) / 2;
    const verticalOffset = (viewportSize.height - scaledHeight) / 2;

    // Calculate the scale factor for the entire video
    const scaleFactorX = scaledWidth / regionBounds.width;
    const scaleFactorY = scaledHeight / regionBounds.height;

    // Use the same scale factor for both dimensions to maintain aspect ratio
    const scaleFactor = Math.min(scaleFactorX, scaleFactorY);

    // Calculate the dimensions of the entire video after scaling
    const fullVideoWidth = originalVideoWidth * scaleFactor;
    const fullVideoHeight = originalVideoHeight * scaleFactor;

    // Calculate the position of the video to show only the desired region
    // We need to position the video so that the region's top-left corner aligns with the container's top-left corner
    const videoLeft = -regionBounds.left * scaleFactor;
    const videoTop = -regionBounds.top * scaleFactor;

    // Store the actual displayed size for debugging
    setActualSize({
      width: scaledWidth,
      height: scaledHeight,
    });

    // Create a container for the video that will be the exact size of the scaled region
    const newContainerStyle = {
      position: 'absolute',
      width: `${scaledWidth - classSpacing.horizontal}px`,
      height: `${scaledHeight - classSpacing.vertical}px`,
      left: `${horizontalOffset}px`,
      top: `${verticalOffset}px`,
      overflow: 'hidden',
      backgroundColor: 'transparent',
    };

    // Log the calculations for debugging
    if (showDebug) {
      console.log('Video positioning calculations:', {
        regionBounds,
        regionAspectRatio,
        viewportAspectRatio,
        viewportSize,
        scaledWidth,
        scaledHeight,
        horizontalOffset,
        verticalOffset,
        scaleFactor,
        fullVideoWidth,
        fullVideoHeight,
        videoLeft,
        videoTop,
        containerStyle: newContainerStyle,
      });
    }
    
    setContainerStyle(newContainerStyle);

    // Position the video within the container to show only the desired region
    setVideoStyle({
      position: 'absolute',
      width: `${fullVideoWidth - classSpacing.horizontal}px`,
      height: `${fullVideoHeight - classSpacing.vertical}px`,
      left: `${videoLeft}px`,
      top: `${videoTop}px`,
      objectFit: 'fill',
    });
  }, [isVideoReady, regionBounds, viewportSize, videoMetadata, classSpacing, showDebug]);

  return {
    containerStyle,
    videoStyle,
    actualSize,
  };
}
